import React, { useEffect, useState } from 'react';

import { CollapseArrow, ExpandArrow } from '@travel/icons/ui';
import { EMPTY_FUNCTION } from '@travel/traveler-core/constants';
import { Animation } from '@travel/ui';
import { cx } from '@travel/utils';

import FlatButton from '../FlatButton';

import styles from './titleAccordion.module.scss';

import type { ReactNode } from 'react';
type CustomIcon = {
  /** Custom icon for expand and collapse state */
  expandIcon?: ReactNode;
  collapseIcon?: ReactNode;
};

type Props = {
  /** string of component id */
  id?: string;
  /** style name to control appearance of this component from parent component*/
  className?: string;
  /** style name for component when it is opened and showing content */
  openClassName?: string;
  /** style name for title (text) */
  titleClassName?: string;
  /** title of the accordion */
  title?: string | ReactNode;
  /** component's children */
  children?: ReactNode;
  /** Should accordion be open */
  isOpen?: boolean;
  /** Call back to be called after component is clicked. */
  onClick?: (id?: string) => void;
  /** flag to allow the useEffect to go through on change in isOpen prop from parent component */
  isControlledByParent?: boolean;
  /** Flag to enable animation when component mounted */
  hasAnimation?: boolean;
} & CustomIcon;

type TitleProps = {
  text: ReactNode;
  titleClassName?: string;
  isOpen: boolean;
  onClick: () => void;
} & CustomIcon;

function Title(props: TitleProps) {
  const { text, titleClassName, isOpen, onClick } = props;
  const expandIcon = props.expandIcon || <ExpandArrow />;
  const collapseIcon = props.collapseIcon || <CollapseArrow />;

  return (
    <FlatButton className={cx(styles.title, titleClassName)} onClick={onClick}>
      <span className={styles.text}>{text}</span>
      <span className={styles.icon}>{isOpen ? collapseIcon : expandIcon}</span>
    </FlatButton>
  );
}

export default function TitleAccordion(props: Props) {
  const {
    id,
    className = '',
    titleClassName,
    openClassName,
    title = '',
    children = null,
    isOpen = false,
    onClick = EMPTY_FUNCTION,
    expandIcon,
    collapseIcon,
    isControlledByParent,
    hasAnimation,
    ...rest
  } = props;
  const [showContent, setShowContent] = useState(isOpen);

  const handleClick = () => {
    setShowContent((currentShowContent) => !currentShowContent);
    onClick(id);
  };

  useEffect(() => {
    if (isControlledByParent) setShowContent(isOpen);
  }, [isControlledByParent, isOpen]);

  return (
    <div
      className={cx(styles.container, className, showContent && openClassName)}
      data-testid="titleAccordion-wrapper"
      {...rest}
    >
      <Title
        text={title}
        isOpen={showContent}
        onClick={handleClick}
        expandIcon={expandIcon}
        collapseIcon={collapseIcon}
        titleClassName={titleClassName}
      />
      {hasAnimation ? (
        <Animation
          isStartAction={showContent}
          type="fadeInDown"
          animationDuration="short"
          wrapperClassName={styles.content}
        >
          {children}
        </Animation>
      ) : (
        showContent && <div className={styles.content}>{children}</div>
      )}
    </div>
  );
}